/**
 * ImpersonationBanner — fixed-top red banner shown while a super-admin is
 * actively impersonating a tenant (M23).
 *
 * Visibility: keyed off `localStorage[IMPERSONATION_STATE_KEY]`. We poll
 * via a Storage event listener AND on a short interval (storage events
 * don't fire in the same tab that wrote the value).
 *
 * On "Exit Impersonation":
 *   1. POST /admin/impersonate/exit using the impersonation JWT (stored
 *      in `auth.store.accessToken`) — the server stamps `ended_at`.
 *   2. Restore the prior super-admin access token saved in
 *      `impersonation.priorToken`.
 *   3. Clear the localStorage flag.
 *   4. Toast + redirect to /super-admin (i.e. /admin) page.
 *
 * Wiring: inserted by the maintainer near the very top of `RootLayout.tsx`
 * — see the WIRING REQUIRED report. Uses a fixed-position container so it
 * sits above the sidebar.
 */
import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  clearImpersonationState,
  readImpersonationState,
  useExitImpersonation,
  type ImpersonationLocalState,
} from '@/features/admin/impersonation.api';
import { useAuthStore } from '@/stores/auth.store';

// We poll localStorage every 2s as well as listening to storage events,
// because the "storage" DOM event doesn't fire in the same tab that
// wrote the value. Two seconds is fast enough for UX, light enough to
// be invisible in the perf trace.
const POLL_INTERVAL_MS = 2_000;

export function ImpersonationBanner(): ReactNode {
  const [state, setState] = useState<ImpersonationLocalState | null>(() =>
    readImpersonationState(),
  );
  const exitMutation = useExitImpersonation();
  const navigate = useNavigate();

  useEffect(() => {
    function refresh(): void {
      setState(readImpersonationState());
    }
    refresh();
    window.addEventListener('storage', refresh);
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      window.removeEventListener('storage', refresh);
      window.clearInterval(id);
    };
  }, []);

  if (!state) return null;

  async function handleExit(): Promise<void> {
    if (!state) return;
    const impersonationToken = useAuthStore.getState().accessToken;
    try {
      if (impersonationToken) {
        await exitMutation.mutateAsync(impersonationToken);
      }
    } catch {
      // Best-effort exit — we still restore the prior token client-side
      // even if the server call failed. The server-side TTL (60 min) will
      // ensure the impersonation JWT eventually dies on its own.
    }

    // Restore the prior super-admin token.
    const restored = state.priorToken;
    const store = useAuthStore.getState();
    if (store.user && store.tenantSlug) {
      store.setAuth(store.user, restored, store.tenantSlug);
    }

    clearImpersonationState();
    setState(null);

    toast.success('Impersonation ended');
    void navigate('/admin');
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[60] flex items-center justify-between gap-3 border-b border-red-700 bg-red-600 px-4 py-2 text-sm font-medium text-white shadow"
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle size={16} className="shrink-0" aria-hidden="true" />
        <span className="truncate">
          You are impersonating <span className="font-semibold">{state.tenantName}</span> as
          super-admin
          <span className="ml-2 hidden text-xs text-red-100 sm:inline">
            (logged in as: {state.userEmail})
          </span>
        </span>
      </div>
      <button
        type="button"
        onClick={() => {
          void handleExit();
        }}
        disabled={exitMutation.isPending}
        className="flex shrink-0 items-center gap-1.5 rounded border border-white/40 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white hover:bg-white/20 disabled:opacity-60"
      >
        <X size={13} aria-hidden="true" />
        Exit Impersonation
      </button>
    </div>
  );
}
